import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Button, Alert, Spinner } from "react-bootstrap";
import { Document, Page, pdfjs } from 'react-pdf';
import { ReactReader } from 'react-reader';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import "../styles/Cookbooks.scss";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function CookbookReader() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cookbook, setCookbook] = useState(null);
  const [accessUrl, setAccessUrl] = useState(null);
  const [epubUrl, setEpubUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // PDF-specific state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(800);

  // ePub-specific state
  const [epubLocation, setEpubLocation] = useState(null);

  useEffect(() => {
    fetchCookbookAccess();

    // Set up auto-refresh for presigned URL (every 55 minutes)
    const refreshInterval = setInterval(() => {
      console.log("Refreshing presigned URL...");
      fetchCookbookAccess();
    }, 55 * 60 * 1000); // 55 minutes

    return () => clearInterval(refreshInterval);
  }, [id]);

  // Handle window resize for PDF
  useEffect(() => {
    const handleResize = () => {
      const width = Math.min(window.innerWidth * 0.9, 1200);
      setPageWidth(width);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (epubUrl) {
        URL.revokeObjectURL(epubUrl);
      }
    };
  }, [epubUrl]);

  const fetchCookbookAccess = async () => {
    try {
      // Get presigned URL
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/access-url`,
        { credentials: "include" }
      );

      if (!response.ok) {
        throw new Error("Failed to access cookbook");
      }

      const data = await response.json();
      setAccessUrl(data.url);

      // Also fetch metadata
      const metaResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}`,
        { credentials: "include" }
      );

      if (metaResponse.ok) {
        const metaData = await metaResponse.json();
        setCookbook(metaData.cookbook);

        // For ePub files, download the entire file as a blob and create a blob URL
        if (metaData.cookbook.file_type === 'epub') {
          const fileResponse = await fetch(data.url);
          if (fileResponse.ok) {
            const blob = await fileResponse.blob();
            const blobUrl = URL.createObjectURL(blob);
            setEpubUrl(blobUrl);
          } else {
            throw new Error('Failed to download ePub file');
          }
        }
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const goToPage = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 1 && pageNum <= numPages) {
      setPageNumber(pageNum);
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading cookbook...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Cookbook</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button onClick={() => navigate('/cookbooks')} variant="outline-danger">
              Back to Library
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!cookbook || !accessUrl) {
    return (
      <Container className="text-center mt-5">
        <Alert variant="warning">Cookbook not found</Alert>
        <Button onClick={() => navigate('/cookbooks')}>Back to Library</Button>
      </Container>
    );
  }

  return (
    <Container fluid className="cookbook-reader">
      {/* Header */}
      <div className="reader-header">
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/cookbooks')}
          size="sm"
        >
          <i className="bi bi-arrow-left me-2"></i>
          Back to Library
        </Button>
        <h3 className="mb-0 ms-3">{cookbook.title}</h3>
      </div>

      {/* PDF Viewer */}
      {cookbook.file_type === 'pdf' && (
        <div className="pdf-viewer">
          <Document
            file={accessUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error("Error loading PDF:", error);
              setError("Failed to load PDF. Please try again.");
            }}
            loading={
              <div className="text-center p-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading PDF...</p>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>

          {/* PDF Controls */}
          {numPages && (
            <div className="pdf-controls">
              <Button
                variant="outline-primary"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={goToPrevPage}
              >
                <i className="bi bi-chevron-left"></i> Previous
              </Button>

              <div className="page-info">
                <span className="me-2">Page</span>
                <input
                  type="number"
                  min="1"
                  max={numPages}
                  value={pageNumber}
                  onChange={(e) => goToPage(e.target.value)}
                  style={{ width: '60px' }}
                  className="form-control form-control-sm d-inline"
                />
                <span className="ms-2">of {numPages}</span>
              </div>

              <Button
                variant="outline-primary"
                size="sm"
                disabled={pageNumber >= numPages}
                onClick={goToNextPage}
              >
                Next <i className="bi bi-chevron-right"></i>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ePub Viewer */}
      {cookbook?.file_type === 'epub' && epubUrl && (
        <div className="epub-viewer" style={{ height: '85vh' }}>
          <ReactReader
            url={epubUrl}
            location={epubLocation}
            locationChanged={(epubcfi) => setEpubLocation(epubcfi)}
            title={cookbook.title}
            showToc={true}
            epubInitOptions={{
              openAs: 'epub'
            }}
            styles={{
              container: {
                height: '100%',
                width: '100%'
              },
              readerArea: {
                backgroundColor: '#fff'
              },
              reader: {
                maxWidth: '800px',
                margin: '0 auto'
              }
            }}
          />
        </div>
      )}

      {/* ePub Loading State */}
      {cookbook?.file_type === 'epub' && !epubUrl && !error && (
        <div className="text-center mt-5">
          <Spinner animation="border" />
          <p className="mt-3">Loading ePub file...</p>
        </div>
      )}
    </Container>
  );
}

export default CookbookReader;
